import {useEffect, useRef} from 'react';
import type {PreviewShortcutClickedDetail, PreviewShortcutTarget} from './preview.types';
import {PREVIEW_SHORTCUT_CLICKED_EVENT} from './preview.types';

/**
 * Runs onClicked when the preview shortcut for target is clicked (see enablePreviewShortcuts). Lets a step 1 field
 * respond with its own local state, so the preview needs no shared state to reach it.
 */
export function usePreviewShortcutClicked(target: PreviewShortcutTarget, onClicked: VoidFunction): void {
  const onClickedRef = useRef(onClicked);

  useEffect(() => {
    onClickedRef.current = onClicked;
  }, [onClicked]);

  useEffect(() => {
    const onShortcutClicked = (event: Event): void => {
      if (isShortcutClickedEventFor(event, target)) {
        onClickedRef.current();
      }
    };

    window.addEventListener(PREVIEW_SHORTCUT_CLICKED_EVENT, onShortcutClicked);
    return (): void => {
      window.removeEventListener(PREVIEW_SHORTCUT_CLICKED_EVENT, onShortcutClicked);
    };
  }, [target]);
}

function isShortcutClickedEventFor(event: Event, target: PreviewShortcutTarget): boolean {
  if (!(event instanceof CustomEvent)) {
    return false;
  }
  const detail: unknown = event.detail;
  return isPreviewShortcutClickedDetail(detail) && detail.target === target;
}

function isPreviewShortcutClickedDetail(detail: unknown): detail is PreviewShortcutClickedDetail {
  return typeof detail === 'object' && detail !== null && 'target' in detail;
}
